import React from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Chip,
  Avatar,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { LogoLink } from "../components/logo/LogoLink";
import { Hidden } from "@material-ui/core";
import DisplacementSphere from "../components/background/DisplacementSphere";
import { ThemeToggle } from "../components/theme/ThemeToggle";
import { FooterText } from "../components/footer/FooterText";
import { ContactFooter } from "../components/footer/ContactFooter";
import { SocialIcons } from "../components/content/SocialIcons";
import { SpeedDials } from "../components/speedDial/SpeedDial";

import { ScrollProgress } from "../components/content/ScrollProgress";
import { CredlyBadge } from "../components/content/CredlyBadge";
import { getSkillIcon } from "../utils/skillIcons";
import { ScrollIndicator } from "../components/content/ScrollIndicator";
import { AnimatedSection } from "../components/content/AnimatedSection";
import Resume from "../settings/resume.json";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    position: "relative",
    pointerEvents: "auto",
  },
  heroSection: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(4),
    zIndex: 1,
  },
  contentSection: {
    position: "relative",
    zIndex: 1,
    backgroundColor: "transparent",
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(4),
  },
  container: {
    maxWidth: "1200px",
  },
  hero: {
    textAlign: "center",
    marginBottom: theme.spacing(6),
    zIndex: 2,
    position: "relative",
    pointerEvents: "auto",
    animation: "$fadeInUp 1s ease-out",
  },
  "@keyframes fadeInUp": {
    from: {
      opacity: 0,
      transform: "translateY(30px)",
    },
    to: {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
  "@keyframes marqueeScroll": {
    "0%": { transform: "translateX(0)" },
    "100%": { transform: "translateX(-50%)" },
  },
  profileImage: {
    width: 200,
    height: 200,
    margin: "0 auto",
    marginBottom: theme.spacing(3),
    border: `4px solid ${theme.palette.primary.main}`,
    boxShadow: "0 8px 32px rgba(0, 191, 191, 0.3)",
    transition: "transform 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
    },
  },
  statsContainer: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(4),
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(6),
    flexWrap: "wrap",
  },
  statCardOld: {
    display: "none",
  },
  section: {
    marginBottom: theme.spacing(6),
  },
  sectionTitle: {
    marginBottom: theme.spacing(3),
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  marqueeContainer: {
    position: "relative",
    overflow: "hidden",
    padding: theme.spacing(1, 0),
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 100,
      background: `linear-gradient(to right, ${theme.palette.background.default} 0%, transparent 100%)`,
      zIndex: 2,
      pointerEvents: "none",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      right: 0,
      top: 0,
      bottom: 0,
      width: 100,
      background: `linear-gradient(to left, ${theme.palette.background.default} 0%, transparent 100%)`,
      zIndex: 2,
      pointerEvents: "none",
    },
    "&:hover $marqueeTrack": {
      animationPlayState: "paused",
    },
  },
  marqueeTrack: {
    display: "flex",
    gap: theme.spacing(2),
    animation: "$marqueeScroll 45s linear infinite",
    width: "max-content",
  },
  marqueeItem: {
    width: 168,
    flexShrink: 0,
    height: 104,
    padding: theme.spacing(1.5),
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.04)"
        : "rgba(0,0,0,0.02)",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)"}`,
    borderRadius: 12,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.75),
    cursor: "default",
    transition: "all 0.3s ease",
    position: "relative",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(0,191,191,0.08)"
          : "rgba(0,191,191,0.05)",
      borderColor: "rgba(0,191,191,0.4)",
      transform: "translateY(-4px)",
      boxShadow: "0 10px 28px rgba(0,191,191,0.18)",
    },
    "&:hover $marqueeLogoImg": {
      filter: "grayscale(0%) brightness(1) opacity(1)",
    },
    "&:hover $marqueeTextLogo": {
      opacity: 1,
      filter: "grayscale(0%)",
    },
    "&:hover $marqueeCaption": {
      color:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.92)"
          : "rgba(0,0,0,0.82)",
    },
    "&:hover $marqueeIndustry": {
      color:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.55)"
          : "rgba(0,0,0,0.55)",
    },
  },
  marqueeLogoImg: {
    maxHeight: 40,
    maxWidth: 120,
    objectFit: "contain",
    filter:
      theme.palette.type === "dark"
        ? "grayscale(100%) brightness(2) opacity(0.45)"
        : "grayscale(100%) brightness(0.15) opacity(0.55)",
    transition: "all 0.35s ease",
  },
  marqueeTextLogo: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    opacity: 0.45,
    filter: "grayscale(100%)",
    transition: "all 0.35s ease",
  },
  marqueeTextMain: {
    fontSize: "1.35rem",
    fontWeight: 800,
    letterSpacing: "-0.04em",
    lineHeight: 1,
  },
  marqueeTextSub: {
    fontSize: "0.58rem",
    fontWeight: 600,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginTop: 2,
    opacity: 0.75,
  },
  marqueeCaption: {
    fontSize: "0.72rem",
    fontWeight: 600,
    textAlign: "center",
    lineHeight: 1.2,
    color:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.55)"
        : "rgba(0,0,0,0.55)",
    transition: "color 0.3s ease",
  },
  marqueeIndustry: {
    fontSize: "0.6rem",
    fontWeight: 500,
    textAlign: "center",
    color:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.28)"
        : "rgba(0,0,0,0.35)",
    transition: "color 0.3s ease",
    letterSpacing: "0.01em",
  },
  certificationCard: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.07)"
        : "rgba(0,0,0,0.03)",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
    backdropFilter: "blur(10px)",
  },
  timelineWrapper: {
    position: "relative",
    paddingLeft: theme.spacing(3),
    "&::before": {
      content: '""',
      position: "absolute",
      left: 7,
      top: 8,
      bottom: 8,
      width: 2,
      background:
        "linear-gradient(180deg, #00bfbf 0%, rgba(0,191,191,0.15) 100%)",
    },
  },
  timelineDot: {
    position: "absolute",
    left: -theme.spacing(3) - 1,
    top: 22,
    width: 14,
    height: 14,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    border: "2px solid rgba(0,191,191,0.3)",
    boxShadow: "0 0 10px rgba(0,191,191,0.5)",
  },
  workCard: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3),
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.04)"
        : "rgba(0,0,0,0.02)",
    backdropFilter: "blur(12px)",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
    borderLeft: "3px solid #00bfbf",
    borderRadius: 8,
    position: "relative",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(0,191,191,0.05)"
          : "rgba(0,191,191,0.04)",
      boxShadow: "0 8px 32px rgba(0, 191, 191, 0.15)",
      borderLeftColor: "#db61a2",
      transform: "translateX(4px)",
    },
  },
  workHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(1.5),
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  workDivider: {
    height: 1,
    background:
      "linear-gradient(90deg, rgba(0,191,191,0.3) 0%, transparent 100%)",
    marginBottom: theme.spacing(1.5),
  },
  highlight: {
    marginBottom: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(0.5),
  },
  highlightDot: {
    color: "#00bfbf",
    marginTop: 2,
    flexShrink: 0,
    fontSize: "0.75rem",
  },
  projectCard: {
    height: "100%",
    padding: theme.spacing(2.5),
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.04)"
        : "rgba(0,0,0,0.02)",
    backdropFilter: "blur(12px)",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
    borderRadius: 8,
    transition: "all 0.3s ease",
    display: "flex",
    flexDirection: "column",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 32px rgba(0, 191, 191, 0.18)",
      borderColor: "rgba(0,191,191,0.4)",
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(0,191,191,0.04)"
          : "rgba(0,191,191,0.05)",
    },
  },
  impactBadge: {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: 20,
    fontSize: "0.72rem",
    fontWeight: 700,
    background:
      "linear-gradient(135deg, rgba(0,191,191,0.15) 0%, rgba(219,97,162,0.15) 100%)",
    border: "1px solid rgba(0,191,191,0.35)",
    color: "#00bfbf",
    letterSpacing: "0.03em",
    marginBottom: theme.spacing(1),
  },
  serviceCard: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.03)"
        : "rgba(0,0,0,0.02)",
    backdropFilter: "blur(12px)",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
    borderRadius: 10,
    overflow: "hidden",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-5px)",
      boxShadow:
        theme.palette.type === "dark"
          ? "0 12px 32px rgba(0,0,0,0.3)"
          : "0 12px 32px rgba(0,0,0,0.12)",
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.06)"
          : "rgba(0,0,0,0.04)",
    },
  },
  serviceTopBar: {
    height: 4,
    width: "100%",
    flexShrink: 0,
  },
  serviceBody: {
    padding: theme.spacing(2.5),
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  serviceIconBubble: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.5rem",
    marginBottom: theme.spacing(1.5),
    flexShrink: 0,
  },
  serviceMetric: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "3px 10px",
    borderRadius: 20,
    fontSize: "0.7rem",
    fontWeight: 700,
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    border: "1px solid",
    letterSpacing: "0.02em",
  },
  serviceTools: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    marginTop: "auto",
    paddingTop: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.07)"}`,
  },
  skillCategoryCard: {
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.03)"
        : "rgba(0,0,0,0.02)",
    backdropFilter: "blur(10px)",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.08)"}`,
    borderRadius: 10,
    overflow: "hidden",
    transition: "all 0.25s ease",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.05)"
          : "rgba(0,0,0,0.03)",
      boxShadow:
        theme.palette.type === "dark"
          ? "0 6px 20px rgba(0,0,0,0.2)"
          : "0 6px 20px rgba(0,0,0,0.08)",
    },
  },
  skillCategoryHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.07)"}`,
  },
  skillCategoryTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 700,
    fontSize: "0.9rem",
  },
  skillLevelBadge: {
    fontSize: "0.62rem",
    fontWeight: 700,
    padding: "2px 8px",
    borderRadius: 20,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    flexShrink: 0,
  },
  skillChipArea: {
    padding: theme.spacing(1.5, 2),
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  statCard: {
    textAlign: "center",
    padding: theme.spacing(2),
    minWidth: 130,
  },
  statNumber: {
    fontSize: "2.2rem",
    fontWeight: "bold",
    background: "linear-gradient(135deg, #00bfbf 0%, #db61a2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },
}));

export const Home = () => {
  const classes = useStyles();
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.type === "dark";

  // Theme-aware color tokens for inline styles
  const chipText = isDark ? "rgba(255,255,255,0.78)" : "rgba(0,0,0,0.72)";
  const chipSubText = isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)";

  const formatDate = (dateString) => {
    if (!dateString) return "Present";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
  };

  return (
    <>
      <div className={classes.root} style={{ pointerEvents: "auto" }}>
        <ScrollProgress />
        <DisplacementSphere />
        <LogoLink />
        <ThemeToggle />
        <Hidden smDown>
          <SocialIcons />
        </Hidden>
        <Hidden mdUp>
          <SpeedDials />
        </Hidden>

        {/* Hero Section */}
        <Box className={classes.heroSection}>
          <Container className={classes.container}>
            <Box className={classes.hero}>
              <Avatar
                src={`/${Resume.basics.picture}`}
                alt={Resume.basics.name}
                className={classes.profileImage}
              />
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                style={{
                  fontWeight: 700,
                  background:
                    "linear-gradient(135deg, #00bfbf 0%, #db61a2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {Resume.basics.name}
              </Typography>
              <Typography
                variant="h5"
                component="h2"
                color="textSecondary"
                gutterBottom
                style={{ fontWeight: 500 }}
              >
                {Resume.basics.label}
              </Typography>
              <Typography
                variant="body1"
                paragraph
                style={{
                  maxWidth: "800px",
                  margin: "20px auto",
                  fontSize: "1.1rem",
                  lineHeight: 1.6,
                }}
              >
                {Resume.basics.description}
              </Typography>

              {/* Availability Badge removed per feedback */}

              {/* View Full Resume removed per request */}

              {/* Stats */}
              <Box className={classes.statsContainer}>
                <Box className={classes.statCard}>
                  <Typography className={classes.statNumber}>
                    {Resume.basics.stats?.yearsExperience || "5+"}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Years Experience
                  </Typography>
                </Box>
                <Box className={classes.statCard}>
                  <Typography className={classes.statNumber}>
                    {Resume.basics.stats?.certifications || "5+"}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Certifications
                  </Typography>
                </Box>
                <Box className={classes.statCard}>
                  <Typography className={classes.statNumber}>
                    {Resume.basics.stats?.majorProjects || "5+"}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Major Projects
                  </Typography>
                </Box>
                <Box className={classes.statCard}>
                  <Typography className={classes.statNumber}>
                    {Resume.basics.stats?.cloudSavings || "$200K+"}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Cloud Cost Saved
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Container>
          <ScrollIndicator />
        </Box>

        {/* Content Sections */}
        <Box className={classes.contentSection}>
          <Container className={classes.container}>
            {/* Trusted by Industry Leaders */}
            {Resume.companies && Resume.companies.length > 0 && (
              <AnimatedSection>
                <Box className={classes.section}>
                  {/* Header row */}
                  <Box
                    display="flex"
                    alignItems="center"
                    flexWrap="wrap"
                    style={{ gap: 12, marginBottom: 8 }}
                  >
                    <Typography
                      variant="h4"
                      className={classes.sectionTitle}
                      style={{ marginBottom: 0 }}
                    >
                      Trusted by Industry Leaders
                    </Typography>
                    <Box
                      style={{
                        padding: "3px 12px",
                        borderRadius: 20,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        border: "1px solid rgba(0,191,191,0.35)",
                        color: "#00bfbf",
                        background: "rgba(0,191,191,0.08)",
                        letterSpacing: "0.04em",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {Resume.companies.length} Client Engagements
                    </Box>
                  </Box>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Delivered cloud infrastructure and platform engineering
                    solutions for leading organizations across industries
                  </Typography>

                  {/* Infinite marquee */}
                  <Box className={classes.marqueeContainer}>
                    <Box className={classes.marqueeTrack}>
                      {[
                        ...Resume.companies,
                        ...Resume.companies,
                        ...Resume.companies,
                      ].map((company, index) => (
                        <a
                          key={index}
                          href={
                            company.website ||
                            `https://www.google.com/search?q=${encodeURIComponent(company.name)}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          <Box
                            className={classes.marqueeItem}
                            style={{ cursor: "pointer" }}
                          >
                            {company.logo ? (
                              <img
                                src={company.logo}
                                alt={company.name}
                                className={classes.marqueeLogoImg}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <Box
                                className={classes.marqueeTextLogo}
                                style={{ color: company.color || "#00bfbf" }}
                              >
                                <span className={classes.marqueeTextMain}>
                                  {company.textLogo ||
                                    company.name.split(" ")[0]}
                                </span>
                                {company.subtitle && (
                                  <span className={classes.marqueeTextSub}>
                                    {company.subtitle}
                                  </span>
                                )}
                              </Box>
                            )}
                            <Typography className={classes.marqueeCaption}>
                              {company.name}
                            </Typography>
                            {company.industry && (
                              <Typography className={classes.marqueeIndustry}>
                                {company.industry}
                              </Typography>
                            )}
                          </Box>
                        </a>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </AnimatedSection>
            )}

            {/* Certifications */}
            {Resume.certifications && Resume.certifications.length > 0 && (
              <AnimatedSection delay={100}>
                <Box className={classes.section}>
                  <Typography variant="h4" className={classes.sectionTitle}>
                    Certifications
                  </Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Industry-recognized credentials validating expertise in
                    cloud and DevOps
                  </Typography>
                  <Grid container spacing={3} justifyContent="center">
                    {Resume.certifications.map((cert, index) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        key={index}
                        style={{ display: "flex", justifyContent: "center" }}
                      >
                        {cert.badgeId ? (
                          <CredlyBadge badgeId={cert.badgeId} />
                        ) : (
                          <Card className={classes.certificationCard}>
                            <CardContent>
                              <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                              >
                                <Typography variant="h6" gutterBottom>
                                  {cert.name}
                                </Typography>
                                <Typography
                                  variant="subtitle1"
                                  color="textSecondary"
                                  gutterBottom
                                  align="center"
                                >
                                  {cert.title}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  align="center"
                                >
                                  {cert.issuer}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  style={{ marginTop: 8 }}
                                >
                                  {formatDate(cert.date)}
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        )}
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </AnimatedSection>
            )}

            {/* Professional Experience */}
            {Resume.work && Resume.work.length > 0 && (
              <AnimatedSection delay={200}>
                <Box className={classes.section}>
                  <Typography variant="h4" className={classes.sectionTitle}>
                    Professional Experience
                  </Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Building and scaling cloud infrastructure for enterprise
                    clients across 5+ years
                  </Typography>
                  <Box className={classes.timelineWrapper}>
                    {Resume.work.map((job, index) => {
                      const isCurrent = !job.endDate;
                      return (
                        <Card key={index} className={classes.workCard}>
                          <Box className={classes.timelineDot} />
                          <CardContent style={{ padding: "16px 20px" }}>
                            {/* Header row */}
                            <Box className={classes.workHeader}>
                              <Box style={{ flex: 1, minWidth: 0 }}>
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  flexWrap="wrap"
                                  style={{ gap: 8, marginBottom: 4 }}
                                >
                                  <Typography
                                    variant="h6"
                                    style={{ fontWeight: 700, lineHeight: 1.3 }}
                                  >
                                    {job.position}
                                  </Typography>
                                  {isCurrent && (
                                    <Chip
                                      label="Current"
                                      size="small"
                                      style={{
                                        background:
                                          "linear-gradient(135deg, #00bfbf, #db61a2)",
                                        color: "#fff",
                                        fontWeight: 700,
                                        fontSize: "0.65rem",
                                        height: 20,
                                      }}
                                    />
                                  )}
                                </Box>
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  flexWrap="wrap"
                                  style={{ gap: 12 }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    style={{
                                      color: "#00bfbf",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {job.company}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 4,
                                    }}
                                  >
                                    📍 {job.location}
                                  </Typography>
                                </Box>
                              </Box>
                              <Chip
                                label={`${formatDate(job.startDate)} – ${formatDate(job.endDate)}`}
                                size="small"
                                variant="outlined"
                                style={{
                                  borderColor: "rgba(0,191,191,0.35)",
                                  color: chipSubText,
                                  fontSize: "0.7rem",
                                  flexShrink: 0,
                                }}
                              />
                            </Box>

                            {/* Divider */}
                            <Box className={classes.workDivider} />

                            {/* Highlights */}
                            {job.highlights && job.highlights.length > 0 && (
                              <Box>
                                {job.highlights.map((highlight, idx) => {
                                  const colonIdx = highlight.indexOf(":");
                                  const hasLabel =
                                    colonIdx > 0 && colonIdx < 40;
                                  const label = hasLabel
                                    ? highlight.substring(0, colonIdx)
                                    : null;
                                  const rest = hasLabel
                                    ? highlight.substring(colonIdx + 1).trim()
                                    : highlight;
                                  return (
                                    <Box
                                      key={idx}
                                      className={classes.highlight}
                                    >
                                      <Typography
                                        variant="body2"
                                        component="span"
                                        className={classes.highlightDot}
                                      >
                                        ▸
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        component="span"
                                        style={{ lineHeight: 1.6 }}
                                      >
                                        {label && (
                                          <span
                                            style={{
                                              color: "#00bfbf",
                                              fontWeight: 600,
                                            }}
                                          >
                                            {label}:{" "}
                                          </span>
                                        )}
                                        {rest}
                                      </Typography>
                                    </Box>
                                  );
                                })}
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                </Box>
              </AnimatedSection>
            )}

            {/* Notable Projects */}
            {Resume.projects && Resume.projects.length > 0 && (
              <AnimatedSection delay={250}>
                <Box className={classes.section}>
                  <Typography variant="h4" className={classes.sectionTitle}>
                    Notable Projects
                  </Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Key engineering initiatives delivering measurable impact
                  </Typography>
                  <Grid container spacing={3}>
                    {Resume.projects.map((project, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Card className={classes.projectCard}>
                          <CardContent
                            style={{
                              padding: "16px",
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            <Box style={{ marginBottom: 8 }}>
                              <span className={classes.impactBadge}>
                                ⚡ {project.impact}
                              </span>
                            </Box>
                            <Typography
                              variant="h6"
                              gutterBottom
                              style={{
                                fontWeight: 700,
                                fontSize: "1rem",
                                lineHeight: 1.3,
                              }}
                            >
                              {project.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              style={{
                                lineHeight: 1.6,
                                flex: 1,
                                marginBottom: 12,
                              }}
                            >
                              {project.description}
                            </Typography>
                            <Box style={{ marginTop: "auto" }}>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                style={{
                                  display: "block",
                                  marginBottom: 6,
                                  opacity: 0.6,
                                }}
                              >
                                {project.client}
                              </Typography>
                              <Box
                                display="flex"
                                flexWrap="wrap"
                                style={{ gap: 4 }}
                              >
                                {project.tech.map((t, i) => (
                                  <Chip
                                    key={i}
                                    label={t}
                                    size="small"
                                    variant="outlined"
                                    style={{
                                      fontSize: "0.65rem",
                                      height: 20,
                                      borderColor: "rgba(0,191,191,0.3)",
                                      color: chipSubText,
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </AnimatedSection>
            )}

            {/* Services & Expertise */}
            {Resume.services && Resume.services.length > 0 && (
              <AnimatedSection delay={300}>
                <Box className={classes.section}>
                  <Typography variant="h4" className={classes.sectionTitle}>
                    Services & Expertise
                  </Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    End-to-end cloud and platform engineering — from bare
                    infrastructure to production-ready ML pipelines
                  </Typography>
                  <Grid container spacing={3}>
                    {Resume.services.map((service, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Card className={classes.serviceCard} elevation={0}>
                          {/* Colored top accent bar */}
                          <Box
                            className={classes.serviceTopBar}
                            style={{
                              background: service.accentColor
                                ? `linear-gradient(90deg, ${service.accentColor}, transparent)`
                                : "linear-gradient(90deg, #00bfbf, transparent)",
                            }}
                          />
                          <Box className={classes.serviceBody}>
                            {/* Icon bubble */}
                            <Box
                              className={classes.serviceIconBubble}
                              style={{
                                background: service.accentColor
                                  ? `${service.accentColor}18`
                                  : "rgba(0,191,191,0.1)",
                                border: `1px solid ${service.accentColor || "#00bfbf"}30`,
                              }}
                            >
                              {service.emoji || "⚙️"}
                            </Box>

                            {/* Title */}
                            <Typography
                              variant="h6"
                              style={{
                                fontWeight: 700,
                                fontSize: "1rem",
                                lineHeight: 1.3,
                                marginBottom: 6,
                              }}
                            >
                              {service.name}
                            </Typography>

                            {/* Description */}
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              style={{ lineHeight: 1.65, flex: 1 }}
                            >
                              {service.description}
                            </Typography>

                            {/* Impact metric */}
                            {service.metric && (
                              <Box
                                className={classes.serviceMetric}
                                style={{
                                  borderColor: service.accentColor
                                    ? `${service.accentColor}50`
                                    : "rgba(0,191,191,0.3)",
                                  color: service.accentColor || "#00bfbf",
                                  background: service.accentColor
                                    ? `${service.accentColor}12`
                                    : "rgba(0,191,191,0.08)",
                                }}
                              >
                                ⚡ {service.metric}
                              </Box>
                            )}

                            {/* Tool chips */}
                            {service.tools && service.tools.length > 0 && (
                              <Box className={classes.serviceTools}>
                                {service.tools.map((tool, i) => (
                                  <Chip
                                    key={i}
                                    label={tool}
                                    size="small"
                                    variant="outlined"
                                    style={{
                                      fontSize: "0.65rem",
                                      height: 20,
                                      borderColor: service.accentColor
                                        ? `${service.accentColor}40`
                                        : "rgba(0,191,191,0.25)",
                                      color: chipSubText,
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </AnimatedSection>
            )}

            {/* Technical Skills & Tools */}
            <AnimatedSection delay={400}>
              <Box className={classes.section}>
                <Typography variant="h4" className={classes.sectionTitle}>
                  Technical Skills & Tools
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Organized by domain across cloud, infrastructure, and platform
                  engineering
                </Typography>

                {Resume.skillCategories ? (
                  <Grid container spacing={2}>
                    {Resume.skillCategories.map((cat, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Box className={classes.skillCategoryCard}>
                          {/* Category header */}
                          <Box
                            className={classes.skillCategoryHeader}
                            style={{
                              borderLeft: `3px solid ${cat.color}`,
                            }}
                          >
                            <Box className={classes.skillCategoryTitle}>
                              <span style={{ fontSize: "1.1rem" }}>
                                {cat.emoji}
                              </span>
                              <Typography
                                variant="body2"
                                style={{ fontWeight: 700 }}
                              >
                                {cat.category}
                              </Typography>
                            </Box>
                            <Box
                              className={classes.skillLevelBadge}
                              style={{
                                background:
                                  cat.level === "Expert"
                                    ? `${cat.color}22`
                                    : "rgba(255,255,255,0.06)",
                                color:
                                  cat.level === "Expert"
                                    ? cat.color
                                    : "rgba(255,255,255,0.45)",
                                border: `1px solid ${cat.level === "Expert" ? cat.color + "50" : "rgba(255,255,255,0.1)"}`,
                              }}
                            >
                              {cat.level}
                            </Box>
                          </Box>

                          {/* Skill chips */}
                          <Box className={classes.skillChipArea}>
                            {cat.items.map((skill, i) => {
                              const iconClass = getSkillIcon(skill);
                              return (
                                <Chip
                                  key={i}
                                  icon={
                                    iconClass.startsWith("text:") ? (
                                      <span
                                        style={{
                                          marginLeft: 6,
                                          fontWeight: 800,
                                          fontSize: "0.6rem",
                                          color: cat.color,
                                          letterSpacing: "-0.02em",
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        {iconClass.slice(5)}
                                      </span>
                                    ) : (
                                      <i
                                        className={iconClass}
                                        style={{
                                          marginLeft: 6,
                                          fontSize: "0.75rem",
                                          color: cat.color,
                                        }}
                                      />
                                    )
                                  }
                                  label={skill}
                                  size="small"
                                  variant="outlined"
                                  style={{
                                    fontSize: "0.72rem",
                                    height: 24,
                                    borderColor: `${cat.color}35`,
                                    color: chipText,
                                    transition: "all 0.2s ease",
                                  }}
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box>
                    {Resume.technicalSkills &&
                      Resume.technicalSkills.map((skill, index) => {
                        const iconClass = getSkillIcon(skill);
                        return (
                          <Chip
                            key={index}
                            icon={
                              <i
                                className={iconClass}
                                style={{ marginLeft: 8 }}
                              />
                            }
                            label={skill}
                            className={classes.chip}
                            size="medium"
                            variant="outlined"
                            style={{ margin: 4 }}
                          />
                        );
                      })}
                  </Box>
                )}
              </Box>
            </AnimatedSection>
          </Container>
        </Box>
        <ContactFooter />
        <FooterText />
      </div>
    </>
  );
};
